import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { FeedbackAPI, KhachLeAPI } from '../../services/feature-api';
import API from '../../services/API';
import { cdnThumb, retryOriginalOnce } from '../../utils/CdnImage';
import GuideButton from '../shared/GuideButton';

// 23.1 Kết quả khách (/feedback-ca-nhan, tên cũ Feedback cá nhân — đổi 16/10/2026) + 24 Kho feedback công ty (/kho-feedback) — anh Bình đặt 15/10/2026.
// Nguồn: PE_Core /feedback, bảng khach_le_feedback. Từ 27/10/2026 KHÔNG còn chờ duyệt: Kho công ty = bảng kho_feedback_cong_ty anh Bình soạn trên admin
// (KhoFeedbackPage cuối file, khối ảnh như trang Combo); ảnh ghi chú ⑥ QL Khách lẻ cũ đã gộp vào hồ sơ (mốc Trong).
// Trang xem = landing page: đầu trang nền màu đậm ngẫu nhiên + ảnh Trước | Sau, thân sáng; chữ Inter, cỡ nhỏ gọn, KHÔNG ánh kim / animation (anh Bình chốt 15/10 tối).

const Icon = ({ name, size = 16, className = '', style }) => (
  <span className={`material-symbols-rounded ${className}`} style={{ fontSize: size, lineHeight: 1, ...style }}>{name}</span>
);
const LOAI = { ban_than: 'Bản thân', dai_ly: 'Đại lý', khach_le: 'Khách lẻ' };
const MOC = [
  { key: 'truoc', ten: 'Trước', tenDai: 'Trước khi dùng', mau: '#64748B', goiY: 'Ảnh lúc chưa dùng sản phẩm' },
  { key: 'trong', ten: 'Trong', tenDai: 'Trong quá trình', mau: '#D97706', goiY: 'Ảnh đang dùng, vài ngày / vài tuần' },
  { key: 'sau', ten: 'Sau', tenDai: 'Sau khi dùng', mau: '#059669', goiY: 'Ảnh kết quả, lời khen của khách' },
];
// Trang xem / danh sách: mỗi lần mở 1 màu đầu trang ngẫu nhiên. nen = dải màu đầu trang, kim = chữ nhấn trên nền đậm, nhat = nền nhạt cho icon
const MAU = [
  { nen: ['#B4232A', '#6E0D0D', '#2A0606'], kim: '#F2C46B', nhat: '#FDECEC' },
  { nen: ['#0F7A55', '#064E3B', '#022019'], kim: '#F2C46B', nhat: '#E6F4EE' },
  { nen: ['#7C2FC0', '#3B0764', '#160428'], kim: '#F2C46B', nhat: '#F1E8FA' },
  { nen: ['#2451D6', '#0B1F4D', '#050B1F'], kim: '#F2C46B', nhat: '#E8EEFC' },
  { nen: ['#C0266D', '#831843', '#2A0616'], kim: '#F9CBBD', nhat: '#FBE9F1' },
  { nen: ['#0E7F9C', '#0E4F5C', '#04191E'], kim: '#F2C46B', nhat: '#E4F3F6' },
  { nen: ['#4B4B53', '#1C1C20', '#050505'], kim: '#F2C46B', nhat: '#EEEEF0' },
  { nen: ['#8F1D3F', '#5A0F27', '#1F0510'], kim: '#F2C46B', nhat: '#F8E7EC' },
];
const nenDam = (t) => `linear-gradient(165deg, ${t.nen[0]} 0%, ${t.nen[1]} 55%, ${t.nen[2]} 100%)`;
const XANH = { c: '#1E5A35', nhat: '#F0F7F2', vien: '#C9E3D2' }; // màu thương hiệu cho danh sách + form
const ngauNhien = (bo = -1) => { let j = bo; while (j === bo) j = Math.floor(Math.random() * MAU.length); return j; };

const fmtNgay = (iso) => (iso ? new Date(iso).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
// Kho công ty: khách lẻ chỉ hiện "Nguyễn T. H." — ảnh khách công khai cho mọi đại lý xem
const che = (ten) => {
  const p = String(ten || '').trim().split(/\s+/).filter(Boolean);
  if (p.length <= 1) return p[0] ? `${p[0][0]}.` : 'Khách hàng';
  return `${p[0]} ${p.slice(1).map((x) => `${x[0]}.`).join(' ')}`;
};
const tenHien = (f, congTy) => (congTy && f.loai_nguoi === 'khach_le' ? che(f.nguoi_ten) : f.nguoi_ten || '—');
// Feedback cũ chưa tách nhóm → coi mọi ảnh là Sau
const anhTheoMoc = (f) => {
  const truoc = f.anh_truoc || [], trong = f.anh_trong || [], sau = f.anh_sau || [];
  return truoc.length || trong.length || sau.length ? { truoc, trong, sau } : { truoc: [], trong: [], sau: f.anh || [] };
};

const NEN_ANH = { maxSizeMB: 0.8, maxWidthOrHeight: 1920, useWebWorker: true, fileType: 'image/jpeg' };
const nenRoiTai = async (file) => {
  let f = file;
  try {
    const c = await imageCompression(file, NEN_ANH);
    f = new File([c], file.name.replace(/\.(heic|heif|png|webp)$/i, '.jpg'), { type: 'image/jpeg' });
  } catch { /* máy cũ không nén được → tải file gốc, máy chủ nén lại qua VPS2 */ }
  return (await API.uploadImage(f)).fileUrl;
};

const useDanhMuc = (accessToken) => {
  const [dm, setDm] = useState({ vanDe: [], sanPham: [] });
  useEffect(() => { if (accessToken) FeedbackAPI.danhMuc(accessToken).then((r) => r.success && r.data && setDm(r.data)); }, [accessToken]);
  return dm;
};

const Anh = ({ src, className, onClick, goc, ...rest }) => (
  <img src={goc ? src : cdnThumb(src)} alt="" loading="lazy" className={className} onClick={onClick} {...rest}
       onError={(e) => { if (!retryOriginalOnce(e.target)) e.target.style.visibility = 'hidden'; }} />
);

// ───────────── Phóng ảnh: mở mượt từ nhỏ ra, nút Đóng trắng to góc trên, vuốt / mũi tên sang ảnh khác ─────────────
const CSS_PHONG = `
@keyframes fbNenMo{from{opacity:0}to{opacity:1}}
@keyframes fbNenDong{from{opacity:1}to{opacity:0}}
@keyframes fbAnhMo{from{opacity:0;transform:scale(.86)}to{opacity:1;transform:none}}
@keyframes fbAnhDong{from{opacity:1;transform:none}to{opacity:0;transform:scale(.9)}}
.fb-nen-mo{animation:fbNenMo .22s ease-out both}.fb-nen-dong{animation:fbNenDong .2s ease-in both}
.fb-anh-mo{animation:fbAnhMo .3s cubic-bezier(.2,.8,.2,1) both}.fb-anh-dong{animation:fbAnhDong .2s ease-in both}
`;
const PhongAnh = ({ ds, i, onClose }) => {
  const [k, setK] = useState(i);
  const [dong, setDong] = useState(false);
  const x0 = useRef(null);
  const tat = useCallback(() => { setDong(true); setTimeout(onClose, 190); }, [onClose]);
  const sang = (d) => setK((v) => (v + d + ds.length) % ds.length);
  useEffect(() => {
    const phim = (e) => { if (e.key === 'Escape') tat(); if (e.key === 'ArrowRight') sang(1); if (e.key === 'ArrowLeft') sang(-1); };
    window.addEventListener('keydown', phim);
    const cu = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', phim); document.body.style.overflow = cu; };
  }); // eslint-disable-line react-hooks/exhaustive-deps
  const a = ds[k];
  return (
    <div className={`fixed inset-0 z-[90] bg-black/90 flex flex-col ${dong ? 'fb-nen-dong' : 'fb-nen-mo'}`} onClick={tat}
         onTouchStart={(e) => { x0.current = e.touches[0].clientX; }}
         onTouchEnd={(e) => { const d = e.changedTouches[0].clientX - (x0.current ?? 0); if (ds.length > 1 && Math.abs(d) > 50) sang(d < 0 ? 1 : -1); }}>
      <style>{CSS_PHONG}</style>
      <div className="flex items-center gap-2 px-4 pt-[calc(14px+env(safe-area-inset-top))] pb-2" onClick={(e) => e.stopPropagation()}>
        {a.nhan && <span className="text-[13px] font-bold text-white rounded-full px-3 py-1" style={{ background: a.mau || 'rgba(255,255,255,.2)' }}>{a.nhan}</span>}
        {ds.length > 1 && <span className="text-[13px] text-white/70">{k + 1} / {ds.length}</span>}
        <div className="flex-1" />
        <button type="button" onClick={tat} aria-label="Đóng ảnh"
                className="h-12 pl-4 pr-5 rounded-full bg-white text-gray-900 font-bold text-[15px] flex items-center gap-1.5 shadow-xl active:scale-95 transition-transform">
          <Icon name="close" size={24} />Đóng
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-3 pb-6 min-h-0 relative">
        <div className="max-w-full max-h-full flex flex-col items-center gap-2 min-h-0" onClick={(e) => e.stopPropagation()}>
          <img key={k} src={a.url} alt="" className={`max-w-full min-h-0 object-contain rounded-xl ${dong ? 'fb-anh-dong' : 'fb-anh-mo'}`} style={{ maxHeight: a.ghiChu ? 'calc(100% - 44px)' : '100%' }} />
          {a.ghiChu && <p className="text-[13px] text-white/90 text-center leading-snug px-4 max-w-[480px]">{a.ghiChu}</p>}
        </div>
        {ds.length > 1 && (
          <>
            <button type="button" aria-label="Ảnh trước" onClick={(e) => { e.stopPropagation(); sang(-1); }}
                    className="absolute left-2 w-11 h-11 rounded-full bg-white/90 text-gray-900 flex items-center justify-center shadow-lg"><Icon name="chevron_left" size={28} /></button>
            <button type="button" aria-label="Ảnh sau" onClick={(e) => { e.stopPropagation(); sang(1); }}
                    className="absolute right-2 w-11 h-11 rounded-full bg-white/90 text-gray-900 flex items-center justify-center shadow-lg"><Icon name="chevron_right" size={28} /></button>
          </>
        )}
      </div>
    </div>
  );
};


// ───────────────────────── Form thêm / sửa (9 mục đánh số) — gọn, chữ nhỏ ─────────────────────────
// Mỗi mục = 1 khối viền xanh rõ + dải tiêu đề nền xanh nhạt (15/10 anh Bình: "section phân biệt không rõ, khó nhìn")
const Muc = ({ so, ten, bat, phu, children }) => (
  <section className="bg-white rounded-xl overflow-hidden border-[1.5px] shadow-[0_2px_8px_-4px_rgba(0,0,0,.15)]" style={{ borderColor: XANH.vien }}>
    <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ background: XANH.nhat, borderColor: XANH.vien }}>
      <span className="w-[22px] h-[22px] rounded-full text-white text-[11.5px] font-bold flex items-center justify-center shrink-0" style={{ background: XANH.c }}>{so}</span>
      <h3 className="text-[13px] font-bold uppercase tracking-[.04em] flex-1" style={{ color: XANH.c }}>{ten}{bat && <span className="text-red-600"> *</span>}</h3>
      {phu && <span className="text-[11.5px] font-medium shrink-0 max-w-[45%] truncate" style={{ color: XANH.c, opacity: .7 }}>{phu}</span>}
    </div>
    <div className="px-3 py-2.5">{children}</div>
  </section>
);
// viewport đã maximum-scale=1 → iPhone không tự phóng khi gõ ô chữ 14px
// ô chữ tự cao theo nội dung (~40 ký tự 1 dòng ở màn 375px), tối thiểu 2 dòng
const soDong = (v) => Math.max(2, String(v).split('\n').reduce((n, d) => n + Math.ceil((d.length || 1) / 40), 0));
const O_CHU = 'w-full text-[14px] leading-snug text-gray-900 border border-gray-200 rounded-lg px-2.5 py-2 outline-none focus:border-[#1E5A35] bg-white placeholder:text-gray-400';
const Tron = ({ on }) => (
  <span className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center shrink-0 ${on ? 'border-[#1E5A35]' : 'border-gray-300'}`}>
    {on && <span className="w-2 h-2 rounded-full bg-[#1E5A35]" />}
  </span>
);
const Chip = ({ on, onClick, children }) => (
  <button type="button" onClick={onClick}
          className={`text-[12.5px] font-medium rounded-full px-2.5 py-1 border ${on ? 'text-white' : 'bg-white text-gray-700 border-gray-200'}`}
          style={on ? { background: XANH.c, borderColor: XANH.c } : {}}>
    {on && '✓ '}{children}
  </button>
);

export const FeedbackForm = ({ accessToken, banDau, nguoiSan, onClose, onSaved }) => {
  const dm = useDanhMuc(accessToken);
  const [nguoi, setNguoi] = useState(() => (banDau
    ? (banDau.loai_nguoi === 'ban_than' ? { banThan: true } : { id: banDau.khach_id, ten: banDau.nguoi_ten, code: banDau.nguoi_code, loai: banDau.loai_nguoi })
    : nguoiSan || null));
  const [q, setQ] = useState('');
  const [ketQuaTim, setKetQuaTim] = useState([]);
  const [vanDe, setVanDe] = useState(banDau?.van_de || []);
  const [vanDeMoi, setVanDeMoi] = useState('');
  const [anh, setAnh] = useState(() => (banDau ? anhTheoMoc(banDau) : { truoc: [], trong: [], sau: [] }));
  const [ghiChu, setGhiChu] = useState(() => ({ ...(banDau?.anh_ghi_chu || {}) })); // url → chữ hiện trên ảnh
  const [dangTai, setDangTai] = useState('');
  const [tinhTrang] = useState(banDau?.tinh_trang || ''); // mục nhập đã ẩn 27/10, giữ giá trị cũ khi lưu
  const [ketQua, setKetQua] = useState(banDau?.ket_qua || banDau?.ghi_chu || '');
  const [sauNgay, setSauNgay] = useState(banDau?.sau_ngay ?? '');
  const [kinhNghiem, setKinhNghiem] = useState(banDau?.kinh_nghiem || '');
  const [sp, setSp] = useState(banDau?.san_pham_ids || []);
  const [lieuTrinhId, setLieuTrinhId] = useState(banDau?.lieu_trinh_id || null);
  const [qSp, setQSp] = useState('');
  const [loi, setLoi] = useState('');
  const [dangLuu, setDangLuu] = useState(false);
  const fileRef = useRef(null);
  const mocChon = useRef('sau');
  const navigate = useNavigate();
  // Kéo ảnh bằng ngón tay / chuột để đổi chỗ, kể cả sang nhóm Trước/Trong/Sau khác (anh Bình 20/10: nút ◀ ▶ chưa tiện).
  // Ảnh đặt touch-action:none nên vuốt trên ảnh = kéo, vuốt chỗ khác = cuộn trang.
  const [keo, setKeo] = useState(null); // { moc, j } ảnh đang cầm
  const [keoDen, setKeoDen] = useState(null); // 'moc:j' ô đang trỏ tới
  const batDauKeo = (e, moc, j) => { e.currentTarget.setPointerCapture(e.pointerId); setKeo({ moc, j }); };
  const dangKeo = (e) => { if (!keo) return; setKeoDen(document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-anh]')?.dataset.anh || null); };
  const thaKeo = () => {
    const tu = keo, den = keoDen; setKeo(null); setKeoDen(null);
    if (!tu || !den) return;
    const [mocDen, jDen] = den.split(':');
    if (mocDen === tu.moc && Number(jDen) === tu.j) return;
    setAnh((a) => { const nguon = [...a[tu.moc]]; const [u] = nguon.splice(tu.j, 1); const dich = mocDen === tu.moc ? nguon : [...a[mocDen]]; dich.splice(Number(jDen), 0, u); return { ...a, [tu.moc]: nguon, [mocDen]: dich }; });
  };
  // Khách lẻ đã nối với đơn lẻ → mục 8 tự lấy SP khách đã mua (id, tên ngắn, tổng số lượng), chọn sẵn hết; bản thân / đại lý vẫn tự chọn.
  const [daMua, setDaMua] = useState([]);
  const [themSp, setThemSp] = useState(false);
  useEffect(() => {
    setDaMua([]); setThemSp(false);
    if (!nguoi || nguoi.banThan || nguoi.loai !== 'khach_le' || !nguoi.id) return undefined;
    let song = true;
    KhachLeAPI.getDon(accessToken, nguoi.id).then((r) => {
      if (!song || !r.success || !Array.isArray(r.data)) return;
      const gop = new Map();
      r.data.forEach((d) => (d.sp || []).forEach((x) => { if (!x.id) return; const c = gop.get(x.id) || { id: x.id, ten: x.ten, sl: 0 }; c.sl += Number(x.sl) || 0; gop.set(x.id, c); }));
      const ds = [...gop.values()].sort((a, b) => b.sl - a.sl);
      setDaMua(ds);
      if (!banDau) setSp((cu) => (cu.length ? cu : ds.map((x) => x.id)));
    });
    return () => { song = false; };
  }, [nguoi, accessToken, banDau]);
  // 1 người chỉ 1 hồ sơ Kết quả (anh Bình chốt 20/10/2026): chọn người đã có hồ sơ → chặn tạo mới, đưa sang hồ sơ cũ để bổ sung ảnh
  const [hoSoCu, setHoSoCu] = useState(null);
  useEffect(() => {
    if (banDau || !nguoi) { setHoSoCu(null); return; }
    FeedbackAPI.cuaToi(accessToken).then((r) => {
      if (!r.success) return;
      setHoSoCu((r.data || []).find((f) => (nguoi.banThan ? f.loai_nguoi === 'ban_than' : Number(f.khach_id) === Number(nguoi.id))) || null);
    });
  }, [banDau, nguoi, accessToken]);

  // Tìm người: gõ là tìm (con cháu đại lý + khách lẻ), để trống = 30 người gần nhất
  useEffect(() => {
    if (nguoi) return undefined;
    const h = setTimeout(() => FeedbackAPI.timNguoi(accessToken, q).then((r) => r.success && setKetQuaTim(Array.isArray(r.data) ? r.data : [])), 250);
    return () => clearTimeout(h);
  }, [q, nguoi, accessToken]);

  const batVanDe = (ten) => setVanDe((ds) => (ds.includes(ten) ? ds.filter((x) => x !== ten) : [...ds, ten]));
  const themVanDe = () => {
    const t = vanDeMoi.trim().replace(/^#/, '');
    if (t && !vanDe.includes(t)) setVanDe([...vanDe, t]);
    setVanDeMoi('');
  };
  const goiYKetQua = dm.vanDe.filter((v) => vanDe.includes(v.ten) && v.ket_qua_goi_y && !ketQua.includes(v.ket_qua_goi_y));

  const moChonAnh = (moc) => { mocChon.current = moc; fileRef.current?.click(); };
  const chonAnh = async (e) => {
    const files = Array.from(e.target.files || []);
    const moc = mocChon.current;
    e.target.value = '';
    if (!files.length) return;
    setLoi('');
    for (let i = 0; i < files.length; i += 1) {
      setDangTai(`${MOC.find((m) => m.key === moc).tenDai}: đang tải ảnh ${i + 1}/${files.length}…`);
      try {
        const url = await nenRoiTai(files[i]);
        setAnh((a) => ({ ...a, [moc]: [...a[moc], url] }));
      } catch (err) { setLoi(err.message); }
    }
    setDangTai('');
  };
  const tongAnh = anh.truoc.length + anh.trong.length + anh.sau.length;

  const dsSp = useMemo(() => {
    const k = qSp.trim().toLowerCase();
    const loc = dm.sanPham.filter((s) => (s.dang_ban !== false || sp.includes(s.id)) && (!k || s.ten.toLowerCase().includes(k)));
    return [...loc.filter((s) => sp.includes(s.id)), ...loc.filter((s) => !sp.includes(s.id))];
  }, [dm.sanPham, qSp, sp]);
  const dsLieuTrinh = dm.lieuTrinh || []; // app chỉ nhận liệu trình admin đã bật "hiện"

  const thieu = !nguoi ? 'Chọn feedback của ai (mục 1)' : hoSoCu ? 'Người này đã có hồ sơ Kết quả — mở hồ sơ cũ (mục 1)' : !vanDe.length ? 'Chọn ít nhất 1 vấn đề (mục 2)' : !tongAnh ? 'Thêm ít nhất 1 ảnh (mục 3)' : '';
  const luu = async () => {
    if (thieu) { setLoi(thieu); return; }
    setDangLuu(true); setLoi('');
    const data = { banThan: !!nguoi.banThan, khachId: nguoi.id, vanDe, anhTruoc: anh.truoc, anhTrong: anh.trong, anhSau: anh.sau, anhGhiChu: ghiChu,
      tinhTrang, ketQua, sauNgay, kinhNghiem, sanPhamIds: sp, lieuTrinhId };
    const r = banDau ? await FeedbackAPI.sua(accessToken, banDau.id, data) : await FeedbackAPI.tao(accessToken, data);
    setDangLuu(false);
    if (r.success) onSaved(banDau ? banDau.id : r.data?.id); else setLoi(r.error || 'Lưu thất bại');
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[#F4F3F0] flex flex-col font-['Inter'] text-gray-900">
      <div className="flex items-center gap-2 px-2.5 h-12 bg-white border-b border-black/[.07] shrink-0">
        <button type="button" onClick={onClose} aria-label="Đóng" className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"><Icon name="close" size={21} /></button>
        <h2 className="flex-1 text-[15px] font-semibold">{banDau ? 'Sửa feedback' : 'Thêm feedback'}</h2>
      </div>

      <div className="flex-1 overflow-y-auto px-2.5 py-3 space-y-3 pb-28">
        <Muc so="1" ten="Feedback của ai" bat>
          {nguoi ? (
            <>
            <div className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 border" style={{ borderColor: XANH.c, background: XANH.nhat }}>
              <Tron on />
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold truncate">{nguoi.banThan ? 'Bản thân tôi' : nguoi.ten}</p>
                <p className="text-[11.5px] text-gray-500">{nguoi.banThan ? 'Feedback của chính tôi' : `${nguoi.code} · ${LOAI[nguoi.loai] || ''}`}</p>
              </div>
              <button type="button" onClick={() => { setNguoi(null); setQ(''); }} className="text-[12px] font-semibold px-2.5 py-1 rounded-md bg-white border border-gray-200 text-gray-700">Đổi</button>
            </div>
            {hoSoCu && (
              <div className="mt-1.5 flex items-center gap-2 rounded-lg px-2.5 py-2 border border-amber-300 bg-amber-50 text-[12px] text-amber-900">
                <span className="flex-1">Người này đã có hồ sơ Kết quả. Một người chỉ 1 hồ sơ — bổ sung ảnh Trước / Trong / Sau vào hồ sơ cũ.</span>
                <button type="button" onClick={() => { onClose(); navigate(`/feedback-ca-nhan/${hoSoCu.id}`); }} className="px-2.5 py-1 rounded-md bg-amber-600 text-white font-bold whitespace-nowrap">Mở hồ sơ cũ</button>
              </div>
            )}
            </>
          ) : (
            <>
              <button type="button" onClick={() => setNguoi({ banThan: true })}
                      className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 border border-amber-200 bg-amber-50/70 text-left">
                <Tron />
                <Icon name="star" size={17} className="text-amber-500" />
                <span className="flex-1 text-[13.5px] font-semibold">Bản thân tôi</span>
                <span className="text-[11px] text-gray-500">feedback của chính bạn</span>
              </button>
              <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 mt-2">
                <Icon name="search" size={17} className="text-gray-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hoặc tìm khách lẻ / đại lý: tên, mã, SĐT…" className="flex-1 py-2 text-[14px] outline-none bg-transparent" />
                {q && <button type="button" onClick={() => setQ('')}><Icon name="close" size={16} className="text-gray-400" /></button>}
              </div>
              <div className="mt-1 max-h-56 overflow-y-auto divide-y divide-gray-100">
                {ketQuaTim.map((u) => (
                  <button key={u.id} type="button" onClick={() => setNguoi({ id: u.id, ten: u.ten, code: u.code, loai: u.role_id === 14 ? 'dai_ly' : 'khach_le' })}
                          className="w-full flex items-center gap-2.5 py-1.5 px-1 text-left active:bg-gray-50">
                    <Tron />
                    <span className="flex-1 min-w-0 text-[13.5px] font-medium truncate">{u.ten || '—'}</span>
                    <span className="text-[11px] text-gray-400 shrink-0">{u.code} · ***{u.sdt3}</span>
                    <span className={`text-[10.5px] font-semibold rounded px-1.5 py-px shrink-0 ${u.role_id === 14 ? 'bg-blue-50 text-blue-700' : 'bg-pink-50 text-pink-700'}`}>{u.role_id === 14 ? 'Đại lý' : 'Khách lẻ'}</span>
                  </button>
                ))}
                {!ketQuaTim.length && <p className="text-[12px] text-gray-400 py-2">Không thấy ai khớp.</p>}
              </div>
            </>
          )}
        </Muc>

        <Muc so="2" ten="Vấn đề" bat phu="chọn được nhiều">
          <div className="flex flex-wrap gap-1.5">
            {[...dm.vanDe.map((v) => v.ten), ...vanDe.filter((t) => !dm.vanDe.some((v) => v.ten === t))].map((t) => (
              <Chip key={t} on={vanDe.includes(t)} onClick={() => batVanDe(t)}>{t}</Chip>
            ))}
          </div>
          <div className="flex gap-1.5 mt-2">
            <input value={vanDeMoi} onChange={(e) => setVanDeMoi(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), themVanDe())}
                   placeholder="Vấn đề khác, ví dụ: Da khô" className={O_CHU} />
            <button type="button" onClick={themVanDe} disabled={!vanDeMoi.trim()} className="px-3 rounded-lg bg-gray-900 text-white text-[12.5px] font-semibold disabled:opacity-25">Thêm</button>
          </div>
        </Muc>

        <Muc so="3" ten="Ảnh hành trình" bat phu={tongAnh ? `${tongAnh} ảnh` : 'chọn nhiều ảnh'}>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={chonAnh} />
          {dangTai && <p className="text-[12px] font-medium mb-1.5" style={{ color: XANH.c }}>{dangTai}</p>}
          {/* 3 hàng ngang Trước → Trong → Sau; dưới mỗi ảnh có ô ghi chú (hiện đè lên ảnh ở trang xem) */}
          <div className="space-y-2">
            {MOC.map((m, i) => (
              <div key={m.key} className="rounded-lg border-[1.5px] px-2 py-2" style={{ borderColor: `${m.mau}66`, background: `${m.mau}0D` }}>
                <div className="flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center shrink-0" style={{ background: m.mau }}>{i + 1}</span>
                  <div className="flex-1 min-w-0 leading-tight">
                    <p className="text-[13px] font-bold" style={{ color: m.mau }}>{m.tenDai}</p>
                    <p className="text-[11px] text-gray-500 truncate">{anh[m.key].length ? `${anh[m.key].length} ảnh` : m.goiY}</p>
                  </div>
                  <button type="button" onClick={() => moChonAnh(m.key)} disabled={!!dangTai}
                          className="shrink-0 inline-flex items-center gap-1 text-[13px] font-bold text-white rounded-lg px-3 py-2 shadow-sm active:scale-95 disabled:opacity-40" style={{ background: m.mau }}>
                    <Icon name="add_photo_alternate" size={18} />Thêm ảnh
                  </button>
                </div>
                {anh[m.key].length > 0 && (
                  <div className="grid grid-cols-4 gap-1.5 mt-2" data-anh={`${m.key}:${anh[m.key].length}`}>
                    {anh[m.key].map((u, j) => {
                      const o = `${m.key}:${j}`; const camDay = keo && keo.moc === m.key && keo.j === j;
                      return (
                      <div key={u + j} data-anh={o} className={`rounded-md ${keoDen === o && !camDay ? 'ring-2' : ''}`} style={{ '--tw-ring-color': m.mau }}>
                        <div className="relative">
                          <Anh src={u} draggable={false} onPointerDown={(e) => batDauKeo(e, m.key, j)} onPointerMove={dangKeo} onPointerUp={thaKeo} onPointerCancel={thaKeo}
                               className={`w-full aspect-square object-cover rounded-md bg-gray-100 select-none cursor-grab ${camDay ? 'opacity-40 scale-95' : ''}`} style={{ touchAction: 'none' }} />
                          <button type="button" aria-label="Bỏ ảnh" onClick={() => setAnh((a) => ({ ...a, [m.key]: a[m.key].filter((_, k) => k !== j) }))}
                                  className="absolute -top-1 -right-1 w-[18px] h-[18px] rounded-full bg-gray-900 text-white flex items-center justify-center"><Icon name="close" size={12} /></button>
                        </div>
                        <input value={ghiChu[u] || ''} onChange={(e) => setGhiChu((g) => ({ ...g, [u]: e.target.value }))} maxLength={160} placeholder="Ghi chú…"
                               className="mt-1 w-full text-[11px] leading-tight bg-white border border-gray-300 rounded px-1.5 py-1 outline-none focus:border-gray-500 placeholder:text-gray-400" />
                      </div>
                      );
                    })}
                  </div>
                )}
                {tongAnh > 1 && i === 0 && <p className="text-[10.5px] text-gray-400 mt-1.5">👆 Giữ ảnh rồi kéo để đổi chỗ, kéo sang nhóm khác cũng được</p>}
              </div>
            ))}
          </div>
        </Muc>

        {/* Mục "Tình trạng ban đầu" ẩn (anh Bình 27/10/2026) — giá trị cũ vẫn giữ (tinhTrang) khi lưu */}
        <Muc so="4" ten="Kết quả đạt được">
          <textarea value={ketQua} onChange={(e) => setKetQua(e.target.value)} rows={soDong(ketQua)} placeholder="Ví dụ: Mụn xẹp hẳn, da đều màu, khách rất vui" className={O_CHU} />
          {goiYKetQua.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              <span className="text-[11px] text-gray-400 self-center">Gợi ý:</span>
              {goiYKetQua.map((v) => (
                <button key={v.id} type="button" onClick={() => setKetQua((k) => (k.trim() ? `${k.trim()}. ${v.ket_qua_goi_y}` : v.ket_qua_goi_y))}
                        className="text-[11.5px] px-2 py-0.5 rounded-full border text-left" style={{ background: XANH.nhat, borderColor: XANH.vien, color: XANH.c }}>+ {v.ket_qua_goi_y}</button>
              ))}
            </div>
          )}
        </Muc>

        <Muc so="5" ten="Có kết quả sau … ngày">
          <div className="flex items-center gap-1.5">
            <input type="number" inputMode="numeric" min="0" value={sauNgay} onChange={(e) => setSauNgay(e.target.value)} className={`${O_CHU} !w-16 text-center font-semibold`} />
            <span className="text-[13px] text-gray-600 mr-auto">ngày</span>
            {[7, 14, 21, 30].map((n) => <Chip key={n} on={Number(sauNgay) === n} onClick={() => setSauNgay(n)}>{n}</Chip>)}
          </div>
        </Muc>

        <Muc so="6" ten="Kinh nghiệm rút ra">
          <textarea value={kinhNghiem} onChange={(e) => setKinhNghiem(e.target.value)} rows={soDong(kinhNghiem)} placeholder="Ví dụ: Dùng đều buổi tối, kiêng nặn mụn, uống đủ nước" className={O_CHU} />
        </Muc>

        <Muc so="7" ten="Sản phẩm" phu={daMua.length ? 'lấy sẵn từ đơn lẻ của khách' : sp.length ? `đã chọn ${sp.length}` : 'chọn được nhiều'}>
          {daMua.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {daMua.map((s) => <Chip key={s.id} on={sp.includes(s.id)} onClick={() => setSp(sp.includes(s.id) ? sp.filter((x) => x !== s.id) : [...sp, s.id])}>{s.ten} <span className="opacity-75">×{s.sl}</span></Chip>)}
            </div>
          )}
          {daMua.length > 0 && !themSp ? (
            <button type="button" onClick={() => setThemSp(true)} className="text-[12px] font-semibold text-gray-500 underline underline-offset-2">+ Thêm sản phẩm khác</button>
          ) : (
            <>
              <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 mb-2">
                <Icon name="search" size={17} className="text-gray-400" />
                <input value={qSp} onChange={(e) => setQSp(e.target.value)} placeholder="Tìm sản phẩm…" className="flex-1 py-2 text-[14px] outline-none" />
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
                {dsSp.filter((s) => !daMua.some((d) => d.id === s.id)).map((s) => <Chip key={s.id} on={sp.includes(s.id)} onClick={() => setSp(sp.includes(s.id) ? sp.filter((x) => x !== s.id) : [...sp, s.id])}>{s.ten}</Chip>)}
              </div>
            </>
          )}
        </Muc>

        {dsLieuTrinh.length > 0 && (
          <Muc so="8" ten="Liệu trình công ty" phu="chọn 1 nếu đúng liệu trình đã dùng">
            <div className="flex flex-wrap gap-1.5">
              {dsLieuTrinh.map((l) => <Chip key={l.id} on={lieuTrinhId === l.id} onClick={() => setLieuTrinhId(lieuTrinhId === l.id ? null : l.id)}>{l.ten}{l.so_ngay ? ` · ${l.so_ngay} ngày` : ''}</Chip>)}
            </div>
          </Muc>
        )}
      </div>

      <div className="absolute bottom-0 inset-x-0 bg-white border-t border-black/[.07] px-2.5 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
        {loi && <p className="text-[12px] text-red-600 mb-1">{loi}</p>}
        <button type="button" onClick={luu} disabled={dangLuu || !!dangTai}
                className="w-full py-2.5 rounded-lg text-white font-semibold text-[14.5px] disabled:opacity-50"
                style={{ background: thieu ? '#9ca3af' : XANH.c }}>
          {dangLuu ? 'Đang lưu…' : thieu || (banDau ? 'Lưu thay đổi' : 'Lưu feedback')}
        </button>
      </div>
    </div>
  );
};

// ───────────────────────── Trang xem 1 feedback — landing page để chụp màn hình gửi khách ─────────────────────────
const NUT_DAU = 'h-8 rounded-full bg-white/15 flex items-center justify-center gap-1 text-[12px] font-medium whitespace-nowrap';
const vienCua = (t) => `${t.nen[0]}33`; // màu nhấn 20% → viền khối

// Khối đánh số: dải tiêu đề riêng (số tròn + chữ in hoa màu nhấn, nền nhạt, kẻ dưới) → tách hẳn khỏi nội dung
const Khoi = ({ so, ten, phu, t, toi, children }) => (
  <section className={`rounded-xl overflow-hidden border ${toi ? 'text-white' : 'bg-white'}`} style={{ borderColor: toi ? 'rgba(255,255,255,.18)' : vienCua(t), background: toi ? 'rgba(255,255,255,.07)' : undefined }}>
    <div className="flex items-center gap-2 px-3 h-9 border-b" style={{ background: toi ? 'rgba(0,0,0,.18)' : t.nhat, borderColor: toi ? 'rgba(255,255,255,.12)' : vienCua(t) }}>
      <span className="w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0" style={{ background: toi ? t.kim : t.nen[0], color: toi ? '#1F1308' : '#fff' }}>{so}</span>
      <h2 className="text-[11.5px] font-bold uppercase tracking-[.1em] flex-1 truncate" style={{ color: toi ? t.kim : t.nen[0] }}>{ten}</h2>
      {phu && <span className={`text-[11px] font-medium shrink-0 ${toi ? 'text-white/70' : 'text-gray-500'}`}>{phu}</span>}
    </div>
    <div className="px-3 py-2.5">{children}</div>
  </section>
);

// Ảnh có ghi chú đè mép dưới (dải tối mờ, chữ trắng) — dùng ở trang xem + thẻ danh sách
const AnhGhiChu = ({ src, ghiChu, className, onClick, goc }) => (
  <button type="button" onClick={onClick} className={`relative block overflow-hidden ${className}`}>
    <Anh src={src} goc={goc} className="absolute inset-0 w-full h-full object-cover bg-gray-100" />
    {ghiChu && (
      <span className="absolute inset-x-0 bottom-0 px-1.5 pt-4 pb-1 text-[10.5px] leading-tight text-white text-left line-clamp-2"
            style={{ background: 'linear-gradient(transparent, rgba(0,0,0,.72))' }}>{ghiChu}</span>
    )}
  </button>
);
// Lưới ảnh 1 mốc: 1 ảnh → rộng 4:3 · 2 ảnh → 2 cột · ≥3 ảnh → 3 cột, tự xuống hàng
const LuoiAnh = ({ ds, ghiChu, onMo }) => (
  <div className={`grid gap-1.5 ${ds.length === 1 ? 'grid-cols-1' : ds.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
    {ds.map((u, i) => <AnhGhiChu key={u + i} src={u} ghiChu={ghiChu[u]} onClick={() => onMo(u)} className={`rounded-lg ${ds.length === 1 ? 'aspect-[4/3]' : 'aspect-square'}`} />)}
  </div>
);

// Kết quả hiển thị: không có kết quả thì ghi_chu = chính chuỗi vấn đề (BE sach()), có người còn gõ kết quả y tên vấn đề ("Da nám")
// → trùng chuỗi vấn đề thì coi như trống, chỉ hiện hashtag 1 lần (anh Bình 20/10/2026)
const ketQuaCua = (f) => { const t = String(f.ket_qua || f.ghi_chu || '').trim(); return t.toLowerCase() === (f.van_de || []).join(', ').toLowerCase() ? '' : t; };

export const FeedbackChiTietPage = ({ accessToken, congTy = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ve = useLocation().state?.ve || (congTy ? '/kho-feedback' : '/feedback-ca-nhan'); // mở từ QL Khách lẻ → quay về đó
  const goc = congTy ? '/kho-feedback' : '/feedback-ca-nhan';
  const [f, setF] = useState(null);
  const [cungKhach, setCungKhach] = useState([]); // mọi lần ghi nhận của cùng khách, cũ → mới
  const [loi, setLoi] = useState('');
  const [iMau, setIMau] = useState(() => ngauNhien());
  const [sua, setSua] = useState(false);
  const [phong, setPhong] = useState(null);
  const t = MAU[iMau];
  const nhan = t.nen[0];

  const load = useCallback(async () => {
    if (!accessToken) return;
    const r = await FeedbackAPI.chiTiet(accessToken, id);
    if (r.success) setF(r.data); else setLoi(r.error || 'Không tải được feedback');
  }, [accessToken, id]);
  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  const khachId = f?.khach_id;
  useEffect(() => {
    if (!accessToken || !khachId) return;
    (congTy ? FeedbackAPI.congTy(accessToken) : FeedbackAPI.cuaToi(accessToken))
      .then((r) => r.success && setCungKhach((r.data || []).filter((x) => x.khach_id === khachId).sort((a, b) => a.id - b.id)));
  }, [accessToken, congTy, khachId]);

  const xoa = async () => {
    if (!window.confirm('Xoá feedback này?')) return;
    const r = await FeedbackAPI.xoa(accessToken, f.id);
    if (r.success) navigate(ve); else setLoi(r.error || 'Xoá thất bại');
  };

  if (!f) return <div className="min-h-screen flex items-center justify-center text-white/80 text-[13px]" style={{ background: nenDam(t) }}>{loi || 'Đang tải…'}</div>;

  const cuaToi = !congTy;
  const nhom = anhTheoMoc(f);
  const ghiChu = f.anh_ghi_chu || {};
  // Danh sách phẳng cho popup: Trước → Trong → Sau, mỗi ảnh mang nhãn mốc + ghi chú
  const tatCa = MOC.flatMap((m) => nhom[m.key].map((url) => ({ url, nhan: m.tenDai, mau: m.mau, moc: m.key, ghiChu: ghiChu[url] })));
  const moPhong = (url, moc) => setPhong(tatCa.findIndex((a) => a.url === url && a.moc === moc));

  // Chỉ vẽ khối có nội dung, đánh số liền 1 → n
  const khoi = [
    ['thong-tin'],
    tatCa.length && ['anh'],
    ketQuaCua(f) && ['ket-qua'],
    f.tinh_trang && ['tinh-trang'],
    f.kinh_nghiem && ['kinh-nghiem'],
    (f.san_pham?.length || f.lieu_trinh_ten) && ['san-pham'],
    cungKhach.length > 1 && ['cac-lan'],
  ].filter(Boolean).map(([k], i) => [k, i + 1]);
  const so = Object.fromEntries(khoi);

  return (
    <div className="min-h-screen bg-[#EEEDEA] font-['Inter'] text-gray-900">
      {/* ── Đầu trang màu đậm: thanh nút + khối 1 Thông tin ── */}
      <header className="text-white pb-4" style={{ background: nenDam(t) }}>
        <div className="max-w-[560px] mx-auto px-3 pt-3 flex items-center gap-1.5">
          <button type="button" onClick={() => navigate(ve)} aria-label="Quay lại" className={`${NUT_DAU} w-8`}><Icon name="arrow_back" size={19} /></button>
          <div className="flex-1" />
          <button type="button" onClick={() => setIMau(ngauNhien(iMau))} className={`${NUT_DAU} px-2.5`}>
            <span className="w-3.5 h-3.5 rounded-full border border-white/70" style={{ background: `conic-gradient(${MAU.map((m) => m.nen[0]).join(',')},${MAU[0].nen[0]})` }} />Đổi màu
          </button>
          {cuaToi && <button type="button" onClick={() => setSua(true)} className={`${NUT_DAU} px-2.5`}><Icon name="edit" size={15} />Sửa</button>}
          {cuaToi && <button type="button" onClick={xoa} aria-label="Xoá" className={`${NUT_DAU} w-8`}><Icon name="delete" size={16} /></button>}
        </div>
        <div className="max-w-[560px] mx-auto px-3 pt-3">
          {/* Gọn, to, không bắt nghĩ (anh Bình 27/10): bỏ chữ "Khách lẻ", bỏ ngày, vấn đề không có dấu # */}
          <Khoi so={so['thong-tin']} ten="Thông tin" t={t} toi>
            <p className="text-[11px] uppercase tracking-[.12em] text-white/60">{f.loai_nguoi === 'ban_than' ? 'Của tôi' : 'Tên khách'}</p>
            <h1 className="text-[26px] font-bold leading-tight break-words">{tenHien(f, congTy)}</h1>
            {f.van_de?.length > 0 && (
              <div className="mt-2.5">
                <p className="text-[11px] uppercase tracking-[.12em] text-white/60 mb-1">Vấn đề gặp phải</p>
                <div className="flex flex-wrap gap-1.5">
                  {f.van_de.map((v) => <span key={v} className="text-[13px] font-semibold rounded-full px-3 py-1 border border-white/25 bg-white/10">{v}</span>)}
                </div>
              </div>
            )}
            <div className="flex gap-4 mt-3 pt-2.5 border-t border-white/15">
              {[f.sau_ngay != null && [f.sau_ngay, 'ngày có kết quả'], [tatCa.length, 'ảnh'], cungKhach.length > 1 && [cungKhach.length, 'lần ghi nhận'], f.san_pham?.length > 0 && [f.san_pham.length, 'sản phẩm']]
                .filter(Boolean).map(([n, ten]) => (
                  <p key={ten} className="leading-none"><span className="text-[20px] font-bold" style={{ color: t.kim }}>{n}</span><span className="text-[10.5px] text-white/65 ml-1">{ten}</span></p>
                ))}
            </div>
          </Khoi>
        </div>
      </header>

      <main className="max-w-[560px] mx-auto px-3 pt-3 pb-5 space-y-3">
        {so.anh && (
          <Khoi so={so.anh} ten="Hình ảnh hành trình" phu={`${tatCa.length} ảnh`} t={t}>
            {/* 3 HÀNG NGANG Trước → Trong → Sau; mỗi hàng: nhãn mốc bên trái, ảnh bên phải tự xuống hàng */}
            <div className="divide-y divide-gray-100">
              {MOC.map((m, i) => {
                const ds = nhom[m.key];
                return (
                  <div key={m.key} className="flex gap-2 py-2 first:pt-0 last:pb-0">
                    <div className="w-[54px] shrink-0 pt-0.5">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: ds.length ? m.mau : '#9CA3AF' }}>
                        <span className="w-4 h-4 rounded-full text-white text-[10px] flex items-center justify-center" style={{ background: ds.length ? m.mau : '#D1D5DB' }}>{i + 1}</span>{m.ten}
                      </span>
                      <span className="block text-[10px] text-gray-400 mt-0.5 pl-5">{ds.length} ảnh</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      {ds.length ? <LuoiAnh ds={ds} ghiChu={ghiChu} onMo={(u) => moPhong(u, m.key)} />
                        : <div className="h-9 rounded-lg border border-dashed border-gray-200 flex items-center justify-center text-[10.5px] text-gray-300">Chưa có ảnh {m.tenDai.toLowerCase()}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Khoi>
        )}

        {so['ket-qua'] && (
          <Khoi so={so['ket-qua']} ten="Kết quả đạt được" phu={f.sau_ngay != null && `sau ${f.sau_ngay} ngày`} t={t}>
            <p className="text-[14.5px] leading-relaxed font-semibold whitespace-pre-line">{ketQuaCua(f)}</p>
          </Khoi>
        )}
        {so['tinh-trang'] && (
          <Khoi so={so['tinh-trang']} ten="Tình trạng ban đầu" t={t}>
            <p className="text-[13.5px] leading-relaxed text-gray-700 whitespace-pre-line">{f.tinh_trang}</p>
          </Khoi>
        )}
        {so['kinh-nghiem'] && (
          <Khoi so={so['kinh-nghiem']} ten="Kinh nghiệm rút ra" t={t}>
            <p className="text-[13.5px] leading-relaxed text-gray-700 whitespace-pre-line">{f.kinh_nghiem}</p>
          </Khoi>
        )}
        {so['san-pham'] && (
          <Khoi so={so['san-pham']} ten="Sản phẩm đồng hành" phu={f.san_pham?.length > 0 && `${f.san_pham.length} sản phẩm`} t={t}>
            {f.lieu_trinh_ten && <p className="text-[12.5px] text-gray-700 mb-2"><Icon name="route" size={14} className="align-[-2px] mr-1" style={{ color: nhan }} />Liệu trình: <b>{f.lieu_trinh_ten}</b></p>}
            <div className="flex flex-wrap gap-1.5">
              {(f.san_pham || []).map((s) => <span key={s.id} className="text-[12.5px] font-medium rounded-lg px-2.5 py-1" style={{ background: t.nhat, color: nhan }}>{s.ten}</span>)}
            </div>
          </Khoi>
        )}

        {/* 1 khách ghi nhiều lần (vd 22/8 → 11/9 → 30/9) → nối thành dòng thời gian */}
        {so['cac-lan'] && (
          <Khoi so={so['cac-lan']} ten="Các lần ghi nhận" phu={`${cungKhach.length} lần`} t={t}>
            <ol>
              {cungKhach.map((x, i) => {
                const dang = x.id === f.id;
                const a = anhTheoMoc(x);
                const u = a.sau[0] || a.trong[0] || a.truoc[0];
                return (
                  <li key={x.id} className={i ? 'border-t border-gray-100' : ''}>
                    <button type="button" disabled={dang} onClick={() => navigate(`${goc}/${x.id}`, { state: { ve } })}
                            className="w-full flex items-center gap-2.5 py-2 text-left">
                      <span className="w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0"
                            style={dang ? { background: nhan, color: '#fff' } : { background: t.nhat, color: nhan }}>{i + 1}</span>
                      {u && <Anh src={u} className="w-11 h-11 rounded-lg object-cover shrink-0 bg-gray-100" />}
                      <span className="flex-1 min-w-0">
                        <span className="block text-[11px] text-gray-400">{fmtNgay(x.created_at)}{x.sau_ngay != null && ` · sau ${x.sau_ngay} ngày`}{dang && <b className="font-semibold" style={{ color: nhan }}> · Đang xem</b>}</span>
                        <span className="block text-[12.5px] text-gray-700 leading-snug line-clamp-2">{x.ket_qua || x.ghi_chu || '—'}</span>
                      </span>
                      {!dang && <Icon name="chevron_right" size={18} className="text-gray-300 shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ol>
          </Khoi>
        )}
      </main>

      {/* ── Chân trang: chữ ký đại lý ── */}
      <footer className="text-white" style={{ background: nenDam(t) }}>
        <div className="max-w-[560px] mx-auto px-4 pt-3.5 pb-[calc(0.9rem+env(safe-area-inset-bottom))] flex items-center gap-3">
          <img src={f.dai_ly_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(f.dai_ly_ten || 'Y')}&background=random`} alt=""
               className="w-10 h-10 rounded-full object-cover border-2" style={{ borderColor: t.kim }} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[.14em] text-white/55">Chia sẻ bởi</p>
            <p className="text-[13.5px] font-semibold truncate">{f.dai_ly_code} - {f.dai_ly_ten}</p>
          </div>
          <p className="text-[13px] font-bold tracking-[.3em]" style={{ color: t.kim }}>YODY</p>
        </div>
      </footer>

      {phong != null && phong >= 0 && <PhongAnh ds={tatCa} i={phong} onClose={() => setPhong(null)} />}
      {sua && <FeedbackForm accessToken={accessToken} banDau={f} onClose={() => setSua(false)} onSaved={() => { setSua(false); setF(null); load(); }} />}
    </div>
  );
};

// ───────────────────────── Danh sách: 23.1 của tôi · 24 kho công ty ─────────────────────────
const KHONG_VAN_DE = 'Chưa gắn vấn đề';
const vanDeCua = (f) => (f.van_de?.length ? f.van_de : [KHONG_VAN_DE]);

// Nhiều lần ghi nhận của cùng 1 khách → 1 thẻ (lấy lần mới nhất làm bìa). ds đã xếp mới → cũ.
const gopTheoKhach = (ds) => {
  const m = new Map();
  ds.forEach((f) => { const k = f.khach_id ?? `f${f.id}`; if (!m.has(k)) m.set(k, []); m.get(k).push(f); });
  return [...m.values()].map((list) => ({ f: list[0], so: list.length }));
};

// Ô ảnh trong thẻ: ảnh đầu + "+N" nếu còn; nhãn mốc góc trên
const OAnh = ({ ds, m, ghiChu, className }) => (
  <div className={`relative overflow-hidden bg-gray-100 ${className}`}>
    <Anh src={ds[0]} className="absolute inset-0 w-full h-full object-cover" />
    <span className="absolute top-1 left-1 text-[9.5px] font-bold uppercase text-white rounded px-1 py-px" style={{ background: m.mau }}>{m.ten}</span>
    {ds.length > 1 && <span className="absolute bottom-1 right-1 text-[10px] font-semibold rounded px-1 py-px bg-black/60 text-white">+{ds.length - 1}</span>}
    {ghiChu[ds[0]] && ds.length === 1 && <span className="absolute inset-x-0 bottom-0 px-1 pt-3 pb-0.5 text-[9.5px] text-white truncate" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,.7))' }}>{ghiChu[ds[0]]}</span>}
  </div>
);
// Cột Trong: 1 → 1 ô · 2 → 2 ô dọc · ≥3 → 2×2 (ô thứ 4 phủ +N)
const CotTrong = ({ ds, mau }) => {
  const hien = ds.slice(0, 4);
  return (
    <div className={`relative grid gap-px h-full ${hien.length === 1 ? 'grid-cols-1' : hien.length === 2 ? 'grid-rows-2' : 'grid-cols-2 grid-rows-2'}`}>
      {hien.map((u, i) => (
        <div key={u + i} className="relative overflow-hidden bg-gray-100 min-h-0">
          <Anh src={u} className="absolute inset-0 w-full h-full object-cover" />
          {i === 3 && ds.length > 4 && <span className="absolute inset-0 bg-black/55 text-white text-[12px] font-semibold flex items-center justify-center">+{ds.length - 3}</span>}
        </div>
      ))}
      <span className="absolute top-1 left-1 text-[9.5px] font-bold uppercase text-white rounded px-1 py-px" style={{ background: mau }}>Trong</span>
    </div>
  );
};

// Thẻ 1 người (đủ bề ngang): Trước | Trong | Sau — Trước và Sau to, Trong thu nhỏ 1–4 ảnh
const TheNguoi = ({ g, congTy, nhan, hienVanDe, onClick }) => {
  const { f, so } = g;
  const nhom = anhTheoMoc(f);
  const ghiChu = f.anh_ghi_chu || {};
  const cot = [nhom.truoc.length && ['truoc', '2fr'], nhom.trong.length && ['trong', '1fr'], nhom.sau.length && ['sau', '2fr']].filter(Boolean);
  const chiTrong = cot.length === 1 && cot[0][0] === 'trong';
  const ketQua = ketQuaCua(f);
  return (
    <button type="button" onClick={onClick} className="w-full text-left bg-white rounded-xl overflow-hidden border shadow-[0_2px_10px_-5px_rgba(0,0,0,.2)] active:opacity-80" style={{ borderColor: `${nhan}40` }}>
      {/* Dải đầu: tên + "Hành trình N ngày" (thông tin bán hàng, thay cho loại · ngày tạo — 15/10 anh Bình) */}
      <div className="flex items-center gap-1.5 px-2.5 py-2" style={{ background: `${nhan}14` }}>
        <p className="text-[14px] font-bold leading-tight truncate" style={{ color: nhan }}>{tenHien(f, congTy)}</p>
        {so > 1 && <span className="shrink-0 text-[10px] font-semibold rounded px-1 py-px text-white" style={{ background: nhan }}>{so} lần</span>}
        {congTy && <span className="text-[10.5px] text-gray-500 truncate shrink-0">ĐL {f.dai_ly_code}</span>}
        {f.sau_ngay != null && <span className="ml-auto shrink-0 text-[11.5px] font-bold text-white rounded-full px-2 py-[3px]" style={{ background: nhan }}>Hành trình {f.sau_ngay} ngày</span>}
      </div>
      {cot.length > 0 && (
        <div className="grid gap-px bg-gray-200 h-[168px]" style={{ gridTemplateColumns: chiTrong ? `repeat(${Math.min(nhom.trong.length, 4)}, 1fr)` : cot.map((c) => c[1]).join(' ') }}>
          {chiTrong
            ? nhom.trong.slice(0, 4).map((u, i) => <OAnh key={u + i} ds={i === 3 ? nhom.trong.slice(3) : [u]} m={MOC[1]} ghiChu={ghiChu} className="h-full" />)
            : cot.map(([k]) => (k === 'trong' ? <CotTrong key={k} ds={nhom.trong} mau={MOC[1].mau} /> : <OAnh key={k} ds={nhom[k]} m={MOC[k === 'truoc' ? 0 : 2]} ghiChu={ghiChu} className="h-full" />))}
        </div>
      )}
      <div className="px-2.5 pt-1.5 pb-2">
        {/* ghi_chu khi không có kết quả = chính chuỗi vấn đề (BE sach()) → không hiện kèm hashtag nữa, kẻo lặp 2 lần (anh Bình 20/10) */}
        {ketQua && <p className="text-[12.5px] text-gray-800 leading-snug line-clamp-2">{ketQua}</p>}
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          {hienVanDe && f.van_de?.length > 0 && (ketQua
            ? <span className="text-[10.5px] font-medium truncate ml-auto" style={{ color: nhan }}>{f.van_de.join(' · ')}</span>
            : f.van_de.map((v) => <span key={v} className="text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ color: nhan, background: `${nhan}14` }}>{v}</span>))}
        </div>
      </div>
    </button>
  );
};

export const FeedbackDanhSachPage = ({ accessToken, congTy = false }) => {
  const navigate = useNavigate();
  const [ds, setDs] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState('');
  const [q, setQ] = useState('');
  const [nhom, setNhom] = useState(false); // mặc định: theo từng người (anh Bình chốt 15/10 tối)
  const [loc, setLoc] = useState('');
  const [them, setThem] = useState(false);
  const [iMau, setIMau] = useState(() => ngauNhien());
  const t = MAU[iMau];
  const goc = congTy ? '/kho-feedback' : '/feedback-ca-nhan';

  const load = useCallback(async () => {
    if (!accessToken) return;
    setDangTai(true);
    const r = congTy ? await FeedbackAPI.congTy(accessToken) : await FeedbackAPI.cuaToi(accessToken);
    if (r.success) setDs(Array.isArray(r.data) ? r.data : []); else setLoi(r.error || 'Lỗi tải dữ liệu');
    setDangTai(false);
  }, [accessToken, congTy]);
  useEffect(() => { load(); }, [load]);

  const hienThi = useMemo(() => {
    const k = q.trim().toLowerCase();
    return ds.filter((f) => (!loc || vanDeCua(f).includes(loc)) &&
      (!k || [f.nguoi_ten, f.nguoi_code, f.ket_qua, f.ghi_chu, f.dai_ly_ten, ...(f.van_de || []), ...(f.san_pham || []).map((s) => s.ten)]
        .some((v) => String(v || '').toLowerCase().includes(k))));
  }, [ds, q, loc]);

  // Nhóm theo vấn đề; đang lọc 1 vấn đề thì CHỈ hiện nhóm đó. Trong mỗi nhóm, nhiều lần của 1 khách gộp 1 thẻ.
  const cacNhom = useMemo(() => {
    const m = new Map();
    hienThi.forEach((f) => vanDeCua(f).forEach((v) => { if (loc && v !== loc) return; if (!m.has(v)) m.set(v, []); m.get(v).push(f); }));
    return [...m.entries()].map(([v, list]) => [v, gopTheoKhach(list), list.length])
      .sort((a, b) => (a[0] === KHONG_VAN_DE) - (b[0] === KHONG_VAN_DE) || b[1].length - a[1].length);
  }, [hienThi, loc]);
  const phang = useMemo(() => gopTheoKhach(hienThi), [hienThi]);
  // Số trên nút lọc = số NGƯỜI có vấn đề đó
  const tatCaVanDe = useMemo(() => {
    const m = new Map();
    ds.forEach((f) => vanDeCua(f).forEach((v) => { if (!m.has(v)) m.set(v, new Set()); m.get(v).add(f.khach_id); }));
    return [...m.entries()].map(([v, s]) => [v, s.size]).sort((a, b) => (a[0] === KHONG_VAN_DE) - (b[0] === KHONG_VAN_DE) || b[1] - a[1]);
  }, [ds]);
  const soNguoi = useMemo(() => new Set(ds.map((f) => f.khach_id)).size, [ds]);

  const Luoi = ({ list, hienVanDe }) => (
    <div className="space-y-2.5">
      {list.map((g) => <TheNguoi key={g.f.id} g={g} congTy={congTy} nhan={t.nen[0]} hienVanDe={hienVanDe} onClick={() => navigate(`${goc}/${g.f.id}`)} />)}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EEEDEA] font-['Inter'] text-gray-900">
      {/* Đầu trang gọn: 1 hàng tiêu đề + nút vuông Thêm, 1 hàng 3 ô số */}
      <div className="text-white px-3 pt-2.5 pb-7" style={{ background: nenDam(t) }}>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/dashboard')} aria-label="Quay lại" className={`${NUT_DAU} w-8`}><Icon name="arrow_back" size={19} /></button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[.1em] leading-none truncate" style={{ color: t.kim }}>{congTy ? '24 · Thành quả công ty' : '23.1 · Thành quả cá nhân'}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <h1 className="text-[17px] font-bold leading-tight truncate">{congTy ? 'Kho feedback công ty' : 'Kết quả khách'}</h1>
              {/* nút Đổi màu thu thành chấm nhỏ cạnh tiêu đề để nhường chỗ cho nút Thêm feedback rộng */}
              <button type="button" onClick={() => setIMau(ngauNhien(iMau))} aria-label="Đổi màu" className="shrink-0 w-5 h-5 rounded-full border border-white/70" style={{ background: `conic-gradient(${MAU.map((m) => m.nen[0]).join(',')},${MAU[0].nen[0]})` }} />
            </div>
          </div>
          {!congTy && (
            <button type="button" onClick={() => setThem(true)} aria-label="Thêm feedback"
                    className="h-12 px-3.5 rounded-xl bg-white text-gray-900 flex items-center gap-1.5 shadow-lg active:scale-95 transition-transform">
              <Icon name="add_a_photo" size={24} style={{ color: t.nen[0] }} />
              <span className="text-[13px] font-bold leading-tight">Thêm<br />feedback</span>
            </button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-1.5 mt-2.5">
          {[[soNguoi, 'người'], [ds.length, 'lần ghi nhận'], [tatCaVanDe.filter(([v]) => v !== KHONG_VAN_DE).length, 'vấn đề']].map(([n, ten]) => (
            <p key={ten} className="rounded-lg bg-white/10 px-2 py-1.5 leading-none"><span className="text-[17px] font-bold" style={{ color: t.kim }}>{n}</span><span className="text-[10.5px] text-white/65 ml-1">{ten}</span></p>
          ))}
        </div>
      </div>

      <div className="px-3 -mt-5 pb-10 max-w-[720px] mx-auto">
        <div className="bg-white rounded-xl shadow-[0_4px_16px_-6px_rgba(0,0,0,.2)] p-2">
          {/* Hướng dẫn (admin Tính năng app → 23.1) để dòng riêng: đặt cạnh tiêu đề thì 375px cụt chữ "Kết quả khách" */}
          {!congTy && (
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-[11.5px] text-gray-500 leading-tight">Lập hồ sơ ngay từ ngày đầu, đồng hành tới khi có kết quả</p>
              <div className="shrink-0 whitespace-nowrap"><GuideButton slug="huong-dan-ket-qua-khach" /></div>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <div className="flex-1 flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5">
              <Icon name="search" size={17} className="text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={congTy ? 'Tìm tên, vấn đề, sản phẩm, đại lý…' : 'Tìm tên khách, vấn đề, sản phẩm…'} className="flex-1 py-1.5 text-[14px] outline-none bg-transparent min-w-0" />
              {q && <button type="button" onClick={() => setQ('')}><Icon name="close" size={16} className="text-gray-400" /></button>}
            </div>
            <button type="button" onClick={() => setNhom(!nhom)} className="shrink-0 h-[34px] text-[11.5px] font-semibold rounded-lg px-2.5 bg-gray-900 text-white flex items-center gap-1">
              <Icon name={nhom ? 'category' : 'person'} size={14} />{nhom ? 'Theo vấn đề' : 'Theo người'}
            </button>
          </div>
          {/* Hashtag xếp nhiều hàng, không phải kéo ngang */}
          <div className="flex flex-wrap gap-1 mt-1.5">
            {tatCaVanDe.map(([v, n]) => (
              <button key={v} type="button" onClick={() => setLoc(loc === v ? '' : v)}
                      className={`text-[11.5px] font-medium rounded-full px-2 py-[3px] border ${loc === v ? 'text-white' : 'bg-white text-gray-700 border-gray-200'}`}
                      style={loc === v ? { background: t.nen[0], borderColor: t.nen[0] } : {}}>
                {v} <span className="opacity-60">{n}</span>{loc === v && ' ✕'}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3">
          {dangTai ? <p className="text-center text-gray-400 text-[13px] py-10">Đang tải…</p>
          : loi ? <p className="text-center text-red-500 text-[13px] py-10">{loi}</p>
          : !hienThi.length ? (
            <p className="text-center text-gray-500 text-[13px] py-10 px-6">
              {ds.length ? 'Không có feedback khớp tìm kiếm.' : congTy ? 'Chưa có feedback nào được công ty duyệt.' : 'Chưa có feedback. Bấm "Thêm" để lưu hành trình đầu tiên.'}
            </p>
          ) : nhom ? (
            <div className="space-y-5">
              {cacNhom.map(([v, the, soLan]) => (
                <section key={v}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-1 h-4 rounded-full shrink-0" style={{ background: t.nen[0] }} />
                    <h2 className="text-[14px] font-bold shrink-0">{v}</h2>
                    <span className="text-[11px] text-gray-500 shrink-0">{the.length} người{soLan > the.length && ` · ${soLan} lần`}</span>
                    <span className="flex-1 h-px bg-gray-300/70" />
                  </div>
                  <Luoi list={the} />
                </section>
              ))}
            </div>
          ) : <Luoi list={phang} hienVanDe />}
        </div>
      </div>

      {them && <FeedbackForm accessToken={accessToken} onClose={() => setThem(false)} onSaved={(id) => { setThem(false); navigate(`${goc}/${id}`); }} />}
    </div>
  );
};


// ───────────────────────── 24 Kho feedback công ty — khối ảnh như trang Combo (anh Bình 27/10/2026) ─────────────────────────
// Nguồn: kho_feedback_cong_ty (admin /kho-feedback-cong-ty soạn: tiêu đề + ảnh + mô tả). Mỗi mục = 1 khối: thanh xanh (STT · tiêu đề · N ảnh) → ảnh ≤ 3/hàng, bấm = phóng to.
export const KhoFeedbackPage = ({ accessToken }) => {
  const navigate = useNavigate();
  const [ds, setDs] = useState(null);
  const [phong, setPhong] = useState(null); // { ds: [{url}], i }
  useEffect(() => { if (accessToken) FeedbackAPI.congTy(accessToken).then((r) => setDs(r.success && Array.isArray(r.data) ? r.data : [])); }, [accessToken]);
  return (
    <div className="min-h-screen bg-[#F4F6F5] font-['Inter'] text-gray-900">
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200 sticky top-0 z-10">
        <button type="button" onClick={() => navigate('/dashboard')} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-[#F0F7F2]" style={{ color: XANH.c }}>
          <Icon name="arrow_back" size={14} /><span>Quay lại</span>
        </button>
        <h1 className="text-sm font-bold text-gray-900 flex-1 truncate">24. Kho feedback công ty</h1>
      </div>
      <div className="max-w-md mx-auto p-3 space-y-3">
        {ds && !ds.length && <p className="text-center text-gray-500 text-[13px] py-8">Công ty chưa đưa feedback nào lên.</p>}
        {ds && ds.map((k, idx) => {
          const cot = Math.min(k.anh.length, 3);
          return (
            <section key={k.id} className="rounded-2xl overflow-hidden bg-white border border-black/[.08] shadow-[0_2px_10px_rgba(0,0,0,.06)]">
              <div className="flex items-center gap-2 px-3 py-2.5 text-white" style={{ background: XANH.c }}>
                <span className="shrink-0 w-7 h-7 rounded-full bg-white flex items-center justify-center text-[14px] font-black" style={{ color: XANH.c }}>{k.stt > 0 ? k.stt : idx + 1}</span>
                <h3 className="flex-1 text-[15px] font-extrabold leading-tight">{k.tieu_de}</h3>
                <span className="shrink-0 px-2 py-0.5 rounded-full bg-white/20 text-[11px] font-bold">{k.anh.length} ảnh</span>
              </div>
              <div className={`grid gap-1.5 p-1.5 ${cot === 1 ? 'grid-cols-1' : cot === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {k.anh.map((u, i) => (
                  <button key={u + i} type="button" onClick={() => setPhong({ ds: k.anh.map((url) => ({ url })), i })}
                          className="aspect-[4/5] rounded-xl overflow-hidden bg-gray-100 border border-black/[.06] active:scale-[.97] transition-transform">
                    <Anh src={u} goc={cot === 1} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              {k.mo_ta && <p className="px-3 pb-3 text-[13px] text-gray-700 leading-relaxed whitespace-pre-line">{k.mo_ta}</p>}
            </section>
          );
        })}
      </div>
      {phong && <PhongAnh ds={phong.ds} i={phong.i} onClose={() => setPhong(null)} />}
    </div>
  );
};
